import type { EngineContext } from "../../context";
import { XMLBuilder } from "fast-xml-parser";
import { resolveIrsIntakeTaxYear, schemaVersionForTaxYear, softwareIdForTaxYear } from "../../constants/taxYear";
import { Record1099C } from "./interface/1099-C-interface";
import {
  generateRandomRecordId,
  generateUniqueTransmissionId,
  generateUniqueSubmissionId,
} from "../../constants/genRecordId";
import {
  buildTransmitterGroup,
  buildVendorGroup,
  buildIssuerDetail,
} from "../../xml/transmitterData";
import { buildSplitRecipientDetail } from "../../constants/recipientIdentity";

/**
 * Formats an amount for IRS reporting by removing decimal points and returning a string
 */
function formatAmount(value?: string): string {
  if (!value) return "0";
  const amount = parseFloat(value);
  if (isNaN(amount)) return "0";
  // Format as whole amount with no decimals
  return Math.round(amount).toString();
}

/**
 * Converts checkbox value (X) to IRS digital boolean format (1 or 0)
 */
function convertCheckboxValue(value?: string): string {
  return value === "X" ? "1" : "0";
}

/**
 * Formats a date string to YYYY-MM-DD format required by the IRS
 */
function formatDate(dateStr?: string): string {
  if (!dateStr) return "";

  // Handle MM/DD/YYYY format
  if (dateStr.includes("/")) {
    const [month, day, year] = dateStr.split("/");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  return dateStr; // Return as is if already in correct format
}

/**
 * Validates an identifiable event code according to IRS requirements
 */
function validateIdentifiableEventCode(code?: string): string {
  const validCodes = ["A", "B", "C", "D", "E", "F", "G", "H"];
  return validCodes.includes(code || "") ? (code as string) : "A"; // Default to "A" if invalid
}

export async function generate1099CXml(
  ctx: EngineContext,
  records: Record1099C[],
  queryParams?: any
) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    return {
      validationResponse: [
        {
          error: true,
          message: "No records found or records input is invalid.",
        },
      ],
      xml: null,
      taxYear: null,
    };
  }

  // Filter records by fileType if specified in query parameters
  let filteredRecords = records;
  if (queryParams && queryParams.fileType) {
    const targetFileType = queryParams.fileType.toUpperCase();

    filteredRecords = records.filter((record) => {
      return (
        record["1099_YEAR"] &&
        (targetFileType === "1099-C" || targetFileType === "1099C")
      );
    });

    if (filteredRecords.length === 0) {
      return {
        validationResponse: [
          {
            error: true,
            message: `No records found with fileType: ${queryParams.fileType}`,
          },
        ],
        xml: null,
        taxYear: null,
      };
    }
  }

  // Fetch the transmitter data
  const transmitterData = ctx.transmitter as any;

  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: true,
    attributeNamePrefix: "@_",
    processEntities: true,
  });

  const taxYear = resolveIrsIntakeTaxYear(filteredRecords[0], ctx.irs.maxIntakeTaxYear);

  // Current date in YYYY-MM-DD format for signature
  const currentDate = new Date().toISOString().substring(0, 10);

  // Build an array of form details, one for each record
  const form1099CDetails = filteredRecords.map((record) => {
    // Safely handle string operations
    const safePersonNameControl = record.RECIPIENT_NAME
      ? (record.RECIPIENT_NAME.substring(0, 4) || "").toUpperCase()
      : "";

    const safeLastName = record.RECIPIENT_NAME2 || "UNKNOWN";
    const safeCity = record.RECIPIENT_CITY;

    // Handle checkboxes properly
    const isDebtorLiable =
      typeof record.BOX5_BORROWER_LIABLE_YES_IND === "string" &&
      record.BOX5_BORROWER_LIABLE_YES_IND === "X"
        ? "1"
        : "0";

    // Create the base form details - following the exact order required by XSD schema
    return {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsd": "http://www.w3.org/2001/XMLSchema",
      // Required elements first
      TaxYr: taxYear.toString(),
      RecordId: (record.id).toString(),
      // Optional elements - using standard IRS code
      IssuerOfficeCd: "ABCD", // Standard code if not provided
      // Required elements
      VoidInd: "0", // Fixed to 0 per schema
      CorrectedInd: record.corrected ? "1" : "0",
      // Optional element for corrections
      ...(record.corrected && {
        PrevSubmittedRecRecipientGrp: {
          PrevSubmittedTIN: record.RECIPIENT_ID_NUMBER.replace(/-/g, ""),
          PrevSubmittedName: record.RECIPIENT_NAME,
        },
      }),
      // Required element
      // TY2026 layout-V2 records file via the shared split builder (business
      // as BusinessName+BUSINESS_TIN); legacy records keep the shape below.
      RecipientDetail: buildSplitRecipientDetail(
        record,
        transmitterData.testFileIndicator === "T"
          ? "000000000"
          : record.RECIPIENT_ID_NUMBER.replace(/-/g, ""),
        {
          USAddress: {
            AddressLine1Txt: record.RECIPIENT_ADDRESS1,
            ...(record.RECIPIENT_ADDRESS2 && {
              AddressLine2Txt: record.RECIPIENT_ADDRESS2,
            }),
            CityNm: safeCity,
            StateAbbreviationCd: record.RECIPIENT_STATE,
            ZIPCd: record.RECIPIENT_ZIP,
          },
        }
      ) ?? {
        TIN:
          transmitterData.testFileIndicator === "T"
            ? "000000000"
            : record.RECIPIENT_ID_NUMBER.replace(/-/g, ""),
        TINSubmittedTypeCd: "INDIVIDUAL_TIN",
        ...(safePersonNameControl && {
          PersonNameControlTxt: safePersonNameControl,
        }),
        PersonName: {
          PersonFirstNm: record.RECIPIENT_NAME,
          ...(safeLastName && { PersonLastNm: safeLastName }),
        },
        MailingAddressGrp: {
          USAddress: {
            AddressLine1Txt: record.RECIPIENT_ADDRESS1,
            ...(record.RECIPIENT_ADDRESS2 && {
              AddressLine2Txt: record.RECIPIENT_ADDRESS2,
            }),
            CityNm: safeCity,
            StateAbbreviationCd: record.RECIPIENT_STATE,
            ZIPCd: record.RECIPIENT_ZIP,
          },
        },
      },
      // Optional elements
      ...(record.ACCOUNT_NUMBER && {
        RecipientAccountNum: record.ACCOUNT_NUMBER,
      }),
      ...(record.BOX1_DATE_OF_IDENTIFIABLE_EVENT && {
        IdentifiableEventDt: formatDate(
          record.BOX1_DATE_OF_IDENTIFIABLE_EVENT instanceof Date
            ? record.BOX1_DATE_OF_IDENTIFIABLE_EVENT.toISOString().split("T")[0]
            : String(record.BOX1_DATE_OF_IDENTIFIABLE_EVENT)
        ),
      }),
      ...(record.BOX2_AMT_OF_DEBT_DISCHARGED && {
        DebtDischargeAmt: formatAmount(record.BOX2_AMT_OF_DEBT_DISCHARGED),
      }),
      ...(record.BOX3_INTEREST_INCL_IN_BOX2 && {
        InterestAmt: formatAmount(record.BOX3_INTEREST_INCL_IN_BOX2),
      }),
      ...(record.BOX4_DEBT_DESCRIPTION &&
        record.BOX4_DEBT_DESCRIPTION !== "" && {
          DebtDesc: record.BOX4_DEBT_DESCRIPTION.substring(0, 100),
        }),
      // Required elements
      DebtorLiableDebtRepaymentInd: isDebtorLiable,
      IdentifiableEventCd: validateIdentifiableEventCode(
        record.BOX6_IDENTIFIABLE_EVENT_CODE
      ),
      // Optional element
      ...(record.BOX7_FAIR_MARKET_VALUE && {
        FairMarketValueAmt: formatAmount(record.BOX7_FAIR_MARKET_VALUE),
      }),
    };
  });

  // Generate unique identifiers for the transmission
  const uniqueTransmissionId = generateUniqueTransmissionId(ctx.irs.tcc);
  const submissionId = generateUniqueSubmissionId();

  // Build the complete envelope structure
  const xmlStructure = {
    "?xml": {
      "@_version": "1.0",
      "@_encoding": "UTF-8",
    },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation":
        "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear),
        UniqueTransmissionId: uniqueTransmissionId,
        TaxYr: taxYear,
        PriorYearDataInd: transmitterData.priorYearDataIndicator || "0",
        TransmissionTypeCd: "O",
        TestCd: transmitterData.testFileIndicator || "T",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: transmitterData.vendorIndicator || "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: 1,
        TotalRecipientFormCnt: form1099CDetails.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission1Grp: [
        {
          IRSubmission1Header: {
            SubmissionId: submissionId,
            TaxYr: taxYear,
            IssuerDetail: buildIssuerDetail(transmitterData),
            ContactPersonInformationGrp: {
              ...(transmitterData.contactName && {
                ContactPersonNm: transmitterData.contactName,
              }),
              ...(transmitterData.contactTelephoneNumber && {
                ContactPhoneNum: transmitterData.contactTelephoneNumber,
              }),
              ...(transmitterData.contactEmailAddress && {
                ContactEmailAddressTxt: transmitterData.contactEmailAddress,
              }),
              ...(transmitterData.ContactFaxNum && {
                ContactFaxNum: transmitterData.ContactFaxNum,
              }),
            },
            FormTypeCd: "1099C",
            ParentFormTypeCd: "1096",
            CFSFElectionInd: "0",
            JuratSignatureGrp: {
              SignatureIntentInd: "1",
              JuratSignaturePIN: "00000",
              SignatureDt: currentDate,
              ...(transmitterData.contactName && {
                JuratPersonTitleTxt: "CFO",
              }),
              ...(transmitterData.contactName && {
                PersonNm: transmitterData.contactName,
              }),
            },
            TotalReportedRcpntFormCnt: form1099CDetails.length.toString(),
            IRSubmission1FormTotals: {
              Form1099CTotalAmtGrp: {
                DebtDischargeAmt: filteredRecords
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(r.BOX2_AMT_OF_DEBT_DISCHARGED || "0"),
                    0
                  )
                  .toFixed(0),
                InterestAmt: filteredRecords
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(r.BOX3_INTEREST_INCL_IN_BOX2 || "0"),
                    0
                  )
                  .toFixed(0),
                FairMarketValueAmt: filteredRecords
                  .reduce(
                    (sum, r) =>
                      sum + parseFloat(r.BOX7_FAIR_MARKET_VALUE || "0"),
                    0
                  )
                  .toFixed(0),
              },
            },
          },
          IRSubmission1Detail: {
            Form1099CDetail: form1099CDetails,
          },
        },
      ],
    },
  };

  try {
    const xml = builder.build(xmlStructure);
    return {
      xml,
      taxYear,
      validationResponse: [], // Empty array means no validation errors
    };
  } catch (error) {
    console.error("Error generating XML:", error);
    return {
      validationResponse: [
        {
          error: true,
          message: "Failed to generate XML",
          details: error instanceof Error ? error.message : String(error),
        },
      ],
      xml: null,
      taxYear,
    };
  }
}